/**
 * Search params stay plain strings, exactly as `URLSearchParams` reads and writes them. TanStack Router's
 * default JSON serializer would turn `?id=123` into a number and write strings back as `?id=%22123%22`,
 * breaking every backend-, email- and OAuth-generated link that points into the app.
 */
export function parseSearch(searchStr: string): Record<string, string> {
  const search: Record<string, string> = {};
  // First value wins for repeated keys, matching `URLSearchParams.get`. `hasOwn`, not `??=`: inherited members
  // (`constructor`, `toString`, …) are not nullish, so `??=` would silently drop a param with such a name.
  for (const [key, value] of new URLSearchParams(searchStr)) {
    if (!Object.hasOwn(search, key)) search[key] = value;
  }
  return search;
}

function toSearchValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value);
  return JSON.stringify(value);
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    const searchValue = toSearchValue(value);
    if (searchValue !== undefined) params.set(key, searchValue);
  }
  const searchStr = params.toString();
  return searchStr ? `?${searchStr}` : "";
}

/** The root route's `validateSearch`: every param is an optional string, so `useSearch` reads are typed. */
export function validateStringSearch(search: Record<string, unknown>) {
  const strings: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(search)) strings[key] = toSearchValue(value);
  return strings;
}

/**
 * Splits an app URL string (`/user/x?a=1#h`) into navigation options for `Link`. TanStack's `to` is a path only —
 * a query string or hash in it is not parsed — so string-built URLs go through here, or `navigate({ href })`.
 * A relative href (`#`, `?tab=x`) stays on the current page, as it did under React Router.
 */
export function linkOptionsFromHref(href: string) {
  // Only path, query and hash matter, so parse against a placeholder origin rather than the page's own location.
  const url = new URL(href, "https://app.invalid");
  return {
    to: href.startsWith("/") ? url.pathname : ".",
    search: parseSearch(url.search),
    hash: url.hash.slice(1) || undefined,
  };
}
